import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader } from "@/components/shared/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Settings as SettingsIcon, Check, CreditCard, Loader2, Sparkles, ShieldCheck,
  ExternalLink, AlertTriangle, Clock, Building2,
} from "lucide-react";
import { toast } from "sonner";

const fmtDate = (unixSeconds) => {
  if (!unixSeconds) return "—";
  return new Date(unixSeconds * 1000).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
};

// ---------------- Billing / Subscription ----------------
const BillingTab = () => {
  const { tenant, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [trialDays, setTrialDays] = useState(14);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cycle, setCycle] = useState("monthly");
  const [checkingOutKey, setCheckingOutKey] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const pollingRef = useRef(false);

  const loadAll = useCallback(async () => {
    try {
      const [plansRes, subRes] = await Promise.all([
        api.get("/billing/plans"),
        api.get("/billing/subscription"),
      ]);
      setPlans(plansRes.data.plans || []);
      setTrialDays(plansRes.data.trial_days || 14);
      setSub(subRes.data);
    } catch {
      toast.error("Couldn't load billing information");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Handle return from Stripe Checkout.
  useEffect(() => {
    const checkout = params.get("checkout");
    const sessionId = params.get("session_id");
    if (checkout === "success" && sessionId && !pollingRef.current) {
      pollingRef.current = true;
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const { data } = await api.get(`/payments/status/${sessionId}`);
          if (data.payment_status === "paid") {
            toast.success("Subscription activated! Welcome aboard.");
            await loadAll();
            await refresh();
            params.delete("checkout"); params.delete("session_id");
            setParams(params, { replace: true });
            return;
          }
        } catch {}
        if (attempts < 6) setTimeout(poll, 1500);
        else {
          params.delete("checkout"); params.delete("session_id");
          setParams(params, { replace: true });
        }
      };
      poll();
    } else if (checkout === "cancel") {
      toast.info("Checkout canceled — no changes were made.");
      params.delete("checkout");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCheckout = async (plan) => {
    const lookupKey = cycle === "yearly" ? plan.yearly_lookup : plan.monthly_lookup;
    setCheckingOutKey(lookupKey);
    try {
      const { data } = await api.post("/billing/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start checkout");
      setCheckingOutKey(null);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const returnUrl = `${window.location.origin}/app/settings?tab=billing`;
      const { data } = await api.post("/billing/portal", { return_url: returnUrl });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't open billing portal");
      setPortalLoading(false);
    }
  };

  if (loading) {
    return <div className="py-16 flex items-center justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" />Loading billing…</div>;
  }

  const isTrialing = sub?.status === "trialing";
  const isTrialExpired = sub?.status === "trial_expired";
  const isActive = sub?.has_subscription && ["active", "trialing"].includes(sub?.status);
  const isPastDue = sub?.status === "past_due";
  const isManaged = sub?.is_managed_externally;

  return (
    <div className="space-y-6" data-testid="billing-tab-content">
      {/* Status banner */}
      {isManaged && (
        <div data-testid="billing-status-managed" className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-violet-50 flex items-center justify-center shrink-0"><Building2 className="h-4.5 w-4.5 text-[#7C3AED]" /></div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900">{tenant?.plan} plan — managed by Aurelo</div>
            <p className="text-xs text-slate-500 mt-0.5">Your workspace is on a sales-assisted or legacy plan. Contact your account manager to make changes.</p>
          </div>
        </div>
      )}
      {isTrialing && (
        <div data-testid="billing-status-trial" className="rounded-2xl border border-violet-200 bg-violet-50/60 p-4 flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl aurelo-gradient flex items-center justify-center shrink-0"><Clock className="h-4.5 w-4.5 text-white" /></div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-slate-900">{sub.trial_days_left} day{sub.trial_days_left === 1 ? "" : "s"} left in your free trial</div>
            <p className="text-xs text-slate-500 mt-0.5">
              {sub.has_subscription ? `You're on ${sub.plan} (${sub.billing_cycle}). ` : ""}
              Choose a plan below to keep full access after your trial ends.
            </p>
          </div>
        </div>
      )}
      {isTrialExpired && (
        <div data-testid="billing-status-trial-expired" className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4 flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-amber-500 flex items-center justify-center shrink-0"><AlertTriangle className="h-4.5 w-4.5 text-white" /></div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900">Your free trial has ended</div>
            <p className="text-xs text-slate-500 mt-0.5">Choose a plan below to keep using Aurelo without interruption.</p>
          </div>
        </div>
      )}
      {isPastDue && (
        <div data-testid="billing-status-past-due" className="rounded-2xl border border-red-200 bg-red-50/70 p-4 flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-red-500 flex items-center justify-center shrink-0"><AlertTriangle className="h-4.5 w-4.5 text-white" /></div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900">Your last payment failed</div>
            <p className="text-xs text-slate-500 mt-0.5">Update your payment method to avoid losing access.</p>
          </div>
        </div>
      )}
      {isActive && sub.status === "active" && (
        <div data-testid="billing-status-active" className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-9 w-9 rounded-xl bg-emerald-500 flex items-center justify-center shrink-0"><ShieldCheck className="h-4.5 w-4.5 text-white" /></div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900">{sub.plan} plan — Active ({sub.billing_cycle})</div>
              <p className="text-xs text-slate-500 mt-0.5">
                {sub.cancel_at_period_end
                  ? <>Cancels on <b>{fmtDate(sub.current_period_end)}</b></>
                  : <>Renews on <b>{fmtDate(sub.current_period_end)}</b></>}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Manage billing (portal) */}
      {sub?.has_subscription && (
        <div className="flex justify-end">
          <Button data-testid="manage-billing-button" variant="outline" onClick={openPortal} disabled={portalLoading}>
            {portalLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CreditCard className="h-4 w-4 mr-1.5" />}
            Manage Billing & Invoices
          </Button>
        </div>
      )}

      {/* Billing cycle toggle */}
      <div className="flex items-center justify-center gap-3">
        <span className={`text-sm font-medium ${cycle === "monthly" ? "text-slate-900" : "text-slate-400"}`}>Monthly</span>
        <Switch data-testid="billing-cycle-toggle" checked={cycle === "yearly"} onCheckedChange={(v) => setCycle(v ? "yearly" : "monthly")} />
        <span className={`text-sm font-medium flex items-center gap-1.5 ${cycle === "yearly" ? "text-slate-900" : "text-slate-400"}`}>
          Yearly <Badge variant="outline" className="border-0 bg-emerald-50 text-emerald-700 text-[10px] font-bold">Save 2 months</Badge>
        </span>
      </div>

      {/* Plan cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((p) => {
          const lookupKey = cycle === "yearly" ? p.yearly_lookup : p.monthly_lookup;
          const price = cycle === "yearly" ? p.yearly_price : p.monthly_price;
          const isCurrent = sub?.has_subscription && sub.plan === p.name && sub.billing_cycle === cycle && !isPastDue;
          const isLoadingThis = checkingOutKey === lookupKey;
          return (
            <div key={p.key} data-testid={`plan-card-${p.key}`}
              className={`relative rounded-2xl border p-5 flex flex-col ${p.popular ? "border-[#7C3AED] shadow-[0_8px_24px_rgba(124,58,237,0.12)]" : "border-slate-200"} ${isCurrent ? "ring-2 ring-emerald-400" : ""} bg-white`}>
              {p.popular && <Badge className="absolute -top-2.5 left-4 aurelo-gradient text-white border-0 text-[10px] font-bold">Most Popular</Badge>}
              {isCurrent && <Badge className="absolute -top-2.5 right-4 bg-emerald-500 text-white border-0 text-[10px] font-bold">Current Plan</Badge>}
              <div className="text-sm font-semibold text-slate-900">{p.name}</div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="text-2xl font-extrabold text-slate-900">${price}</span>
                <span className="text-xs text-slate-400">/{cycle === "yearly" ? "yr" : "mo"}</span>
              </div>
              <ul className="mt-4 space-y-2 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs text-slate-600"><Check className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" />{f}</li>
                ))}
              </ul>
              <Button
                data-testid={`choose-plan-${p.key}`}
                className={`mt-5 w-full ${isCurrent ? "" : "aurelo-gradient text-white"}`}
                variant={isCurrent ? "outline" : "default"}
                disabled={isCurrent || isLoadingThis}
                onClick={() => startCheckout(p)}
              >
                {isLoadingThis ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
                {isCurrent ? "Current Plan" : isLoadingThis ? "Redirecting…" : "Choose Plan"}
              </Button>
            </div>
          );
        })}
        {/* Enterprise (sales-assisted, no self-serve checkout) */}
        <div data-testid="plan-card-enterprise" className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col">
          <div className="text-sm font-semibold text-slate-900">Enterprise</div>
          <div className="mt-2 flex items-baseline gap-1"><span className="text-2xl font-extrabold text-slate-900">Custom</span></div>
          <ul className="mt-4 space-y-2 flex-1">
            {["SSO & white-label", "Dedicated success", "API & webhooks", "Priority support"].map((f) => (
              <li key={f} className="flex items-start gap-2 text-xs text-slate-600"><Check className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" />{f}</li>
            ))}
          </ul>
          <Button data-testid="contact-sales-button" variant="outline" className="mt-5 w-full" onClick={() => window.open("mailto:sales@aurelo.com?subject=Aurelo%20Enterprise", "_blank")}>
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" />Contact Sales
          </Button>
        </div>
      </div>

      <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-1.5">
        <Sparkles className="h-3 w-3" /> New subscriptions include a {trialDays}-day free trial. Payments securely processed by Stripe. Cancel anytime.
      </p>
    </div>
  );
};

export default function Settings() {
  const { tenant, refresh } = useAuth();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [activeTab, setActiveTab] = useState(params.get("tab") === "billing" ? "billing" : "org");
  useEffect(() => { if (tenant) { setName(tenant.name); setCurrency(tenant.currency || "USD"); } }, [tenant]);
  useEffect(() => { if (params.get("tab") === "billing") setActiveTab("billing"); }, [params]);
  const save = async () => { await api.put("/tenant", { name, currency }); await refresh(); toast.success("Settings saved"); };
  const features = tenant?.features || {};

  return (
    <div>
      <PageHeader title="Settings" subtitle="Manage your workspace" icon={SettingsIcon} />
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="org" data-testid="settings-tab-org">Organization</TabsTrigger>
          <TabsTrigger value="ai" data-testid="settings-tab-ai">AI</TabsTrigger>
          <TabsTrigger value="billing" data-testid="settings-tab-billing">Billing</TabsTrigger>
          <TabsTrigger value="branding" data-testid="settings-tab-branding">Branding</TabsTrigger>
        </TabsList>
        <TabsContent value="org" className="mt-4">
          <div className="rounded-2xl bg-white border border-slate-200 p-5 max-w-lg space-y-4">
            <div><Label>Organization name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3"><div><Label>Currency</Label><Input value={currency} onChange={(e) => setCurrency(e.target.value)} className="mt-1" /></div><div><Label>Timezone</Label><Input value={tenant?.timezone || ""} disabled className="mt-1" /></div></div>
            <Button data-testid="save-settings" className="aurelo-gradient text-white" onClick={save}>Save Changes</Button>
          </div>
        </TabsContent>
        <TabsContent value="ai" className="mt-4">
          <div className="rounded-2xl bg-white border border-slate-200 p-5 max-w-lg space-y-3">
            {[["ai_copilot", "Aurelo AI Copilot"], ["ai_sdr", "AI SDR Agents"], ["advanced_reports", "Advanced Reports"], ["sequences", "Sequences"], ["custom_objects", "Custom Objects"]].map(([k, l]) => (
              <div key={k} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0"><span className="text-sm font-medium">{l}</span><Switch checked={!!features[k]} disabled /></div>
            ))}
            <p className="text-xs text-slate-400 pt-2">AI features are provisioned by your plan. Contact sales to change entitlements.</p>
          </div>
        </TabsContent>
        <TabsContent value="billing" className="mt-4">
          <BillingTab />
        </TabsContent>
        <TabsContent value="branding" className="mt-4">
          <div className="rounded-2xl bg-white border border-slate-200 p-5 max-w-lg"><p className="text-sm text-slate-500">White-label branding (custom logo, colors, domain & email sender) is available on the Enterprise plan.</p></div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
